import React, { useCallback, useMemo, useState } from 'react'
import { Button, Col, Dropdown, Menu, Row } from 'antd'
import { useSelector } from 'react-redux'
import moment from 'moment'

import { useT } from 'src/core/hooks/use-translation'
import { ApplicationState } from 'src/store'
import {
  RequestQuoteAirFare,
  ResumeAirRequestQuoteAirs,
  FullRequestAir
} from 'src/types'
import { Text } from 'src/components/Ant'
import CurrencyMask from 'src/components/Formik/InputCurrency/mask'
import Images from 'src/config/images'

import { ConnectionsText, SecondaryText, StrongText } from '../../styles'

import { PickFareProps } from '../SearchResultCard'

import TagFamily from './tag-family'

type Family = Pick<
  RequestQuoteAirFare,
  'baggage_amount' | 'baggage_weight' | 'baggage' | 'family'
>

type Props = PickFareProps & {
  searchKey: string
  item: ResumeAirRequestQuoteAirs
}

function HeaderItem({ selectedFares, onPickFare, searchKey, item }: Props) {
  const { air } = useSelector((state: ApplicationState) => ({
    air: state.air
  }))

  const [activeFamily, setActiveFamily] = useState<Family>({
    baggage_amount: item.request_quote_air_fares[0].baggage_amount,
    baggage_weight: item.request_quote_air_fares[0].baggage_weight,
    baggage: item.request_quote_air_fares[0].baggage,
    family: item.request_quote_air_fares[0].family
  })
  const [activeTotalValue, setActiveTotalValue] = useState<number>(
    item.request_quote_air_fares[0].total_value
  )

  const FormatMoment = useCallback(
    (value: number, format: string) => moment.unix(value).utc().format(format),
    []
  )

  const groupFamily = useMemo(() => {
    const families: RequestQuoteAirFare[] = []

    item.request_quote_air_fares.forEach(fare => {
      if (families.findIndex(({ family }) => family === fare.family) <= -1) {
        families.push(fare)
      }
    })
    setActiveFamily({
      baggage_amount: families[0].baggage_amount,
      baggage_weight: families[0].baggage_weight,
      baggage: families[0].baggage,
      family: families[0].family
    })
    return families
  }, [item])

  const { t } = useT()

  return (
    <Row align="middle" gutter={24} justify="center" type="flex">
      <Col span={3}>
        <Row align="middle" justify="center" type="flex">
          <img
            alt={item.company}
            src={Images.airlines[item.company] || Images.noImage}
            style={{ maxHeight: 45, maxWidth: 45 }}
          />
        </Row>
      </Col>
      <Col span={5}>
        <Row
          align="middle"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <SecondaryText>
            {FormatMoment(item.departure_date, 'DD/MM/YYYY')}
          </SecondaryText>
          <StrongText>{FormatMoment(item.departure_date, 'HH:mm')}</StrongText>
          <SecondaryText>{item.air_origin}</SecondaryText>
        </Row>
      </Col>
      <Col span={5}>
        <Row
          align="middle"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <ConnectionsText connections={item.stops}>
            {`${item.stops ? item.stops : ''} ${t(
              `web.services.airs.${item.stops ? 'stop' : 'direct'}`
            )}${item.stops > 1 ? 's' : ''}`}
          </ConnectionsText>
          <SecondaryText>
            <Text title="web.services.airs.form.flight" onlyText />{' '}
            {item.flight_code}
          </SecondaryText>
        </Row>
      </Col>
      <Col span={5}>
        <Row
          align="middle"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <SecondaryText>
            {FormatMoment(item.arrival_date, 'DD/MM/YYYY')}
          </SecondaryText>
          <StrongText>{FormatMoment(item.arrival_date, 'HH:mm')}</StrongText>
          <SecondaryText>{item.air_destiny}</SecondaryText>
        </Row>
      </Col>
      <Col span={6}>
        <Row
          align="top"
          justify="center"
          type="flex"
          style={{ flexDirection: 'column' }}
        >
          <Dropdown
            // disabled={needDisable}
            overlay={
              <Menu
                data-test-id={`air-result-family-list-${item.request_quote_air_hash}`}
                onClick={value => {
                  const selected = groupFamily.find(
                    ({ family }) => family === value.key
                  )
                  setActiveFamily({
                    baggage_amount: selected.baggage_amount,
                    baggage_weight: selected.baggage_weight,
                    baggage: selected.baggage,
                    family: selected.family
                  })
                  setActiveTotalValue(selected.total_value)
                  value.domEvent.stopPropagation()
                }}
              >
                {groupFamily.map(fare => (
                  <Menu.Item
                    data-test-id={`air-result-family-options-${item.request_quote_air_hash}`}
                    key={fare.family}
                  >
                    {TagFamily({
                      baggage_amount: fare.baggage_amount,
                      baggage_weight: fare.baggage_weight,
                      baggage: fare.baggage,
                      family: fare.family
                    })}
                  </Menu.Item>
                ))}
              </Menu>
            }
            trigger={['hover']}
          >
            <Button
              block
              icon="down"
              data-test-id={`air-result-family-action-${item.request_quote_air_hash}`}
              onClick={event => {
                event.stopPropagation()
              }}
              style={{ marginBottom: 8 }}
              type="dashed"
            >
              {TagFamily({
                baggage_amount: activeFamily.baggage_amount,
                baggage_weight: activeFamily.baggage_weight,
                baggage: activeFamily.baggage,
                family: activeFamily.family
              })}
            </Button>
          </Dropdown>
          <Dropdown
            // disabled={needDisable}
            overlay={
              <Menu
                onClick={value => {
                  value.domEvent.stopPropagation()
                  const findTotalValue = item.request_quote_air_fares.find(
                    ({ request_quote_fare_hash }) =>
                      request_quote_fare_hash === value.key
                  )
                  setActiveTotalValue(findTotalValue.total_value)

                  const fullRequestAir: FullRequestAir = {
                    customer_supplier_id: findTotalValue.customer_supplier_id,
                    message_id: [...air.searches[searchKey].message_id],
                    request_quote_hash: findTotalValue.request_quote_hash,
                    request_quote_air_route_hash:
                      findTotalValue.request_quote_air_route_hash,
                    request_quote_air_hash: [
                      findTotalValue.request_quote_air_hash
                    ],
                    request_quote_air_fare_hash: [
                      findTotalValue.request_quote_fare_hash
                    ],
                    justification: []
                  }

                  onPickFare(fullRequestAir, searchKey)

                  // testAirpolicy({
                  //   fare: findTotalValue,
                  //   airPolicyRule,
                  //   minFareJustification
                  // })
                }}
              >
                {item.request_quote_air_fares
                  .filter(({ family }) => family === activeFamily.family)
                  .map(price => (
                    <Menu.Item key={price.request_quote_fare_hash}>
                      {`BRL ${
                        CurrencyMask({
                          formatStr: true,
                          value: price.total_value
                        }).maskedValue
                      }`}
                    </Menu.Item>
                  ))}
              </Menu>
            }
            trigger={['hover']}
          >
            <Button
              block
              icon="down"
              onClick={event => {
                // If you don't want click extra trigger collapse, you can prevent this:
                event.stopPropagation()
              }}
              style={{ color: '#34a853', marginBottom: 8 }}
              type="dashed"
            >
              {`BRL ${
                CurrencyMask({
                  formatStr: true,
                  value: activeTotalValue
                }).maskedValue
              }
              `}
            </Button>
          </Dropdown>
        </Row>
      </Col>
    </Row>
  )
}

export default HeaderItem
